import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Search, Save, Send, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { recuentosAPI, ESTADOS, ZONAS, formatFechaHora, parseCantidad, fmtNum, errorMsg } from '@/lib/recuentos';
import { IngredienteRow } from './IngredienteRow';
import { EnviarDialog } from './EnviarDialog';

const draftKey = (id) => `recuento_draft_${id}`;

const valuesFromItems = (items) => Object.fromEntries(items.map((i) => [i.ingrediente_id, {
  cantidad: fmtNum(i.cantidad), packs: fmtNum(i.cantidad_packs), sueltos: fmtNum(i.cantidad_sueltos),
}]));

const buildItems = (items, values) => items.map((i) => {
  const v = values[i.ingrediente_id];
  if (i.factor_alternativa) {
    const packs = parseCantidad(v.packs);
    const sueltos = parseCantidad(v.sueltos);
    return { ingrediente_id: i.ingrediente_id, cantidad_packs: packs, cantidad_sueltos: sueltos, cantidad: null };
  }
  return { ingrediente_id: i.ingrediente_id, cantidad: parseCantidad(v.cantidad) };
});

const isCounted = (item, v) => (item.factor_alternativa ? v.packs !== '' || v.sueltos !== '' : v.cantidad !== '');

const useHeaderHeight = () => {
  const [h, setH] = useState(0);
  useEffect(() => {
    const header = Array.from(document.querySelectorAll('header')).find((el) => el.offsetParent !== null);
    if (header) setH(header.offsetHeight);
  }, []);
  return h;
};

export default function RecuentoFormPage({ basePath }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const headerH = useHeaderHeight();
  const [rec, setRec] = useState(null);
  const [values, setValues] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [validacion, setValidacion] = useState(null);
  const [sending, setSending] = useState(false);
  const autosaveRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await recuentosAPI.get(id);
      setRec(data);
      const stored = data.estado === 'borrador' ? JSON.parse(localStorage.getItem(draftKey(id)) || 'null') : null;
      const local = stored && stored.base === data.updated_at ? stored.values : null;
      if (stored && !local) localStorage.removeItem(draftKey(id));
      setValues(local ? { ...valuesFromItems(data.items), ...local } : valuesFromItems(data.items));
      setDirty(!!local);
    } catch (e) {
      toast.error(errorMsg(e, 'No se pudo cargar el recuento'));
      navigate(basePath);
    }
  }, [id, basePath, navigate]);

  useEffect(() => { load(); }, [load]);

  const editable = rec && (rec.estado === 'borrador' || isAdmin);
  const isCorrection = rec && rec.estado !== 'borrador' && isAdmin;

  const save = useCallback(async (silent = false) => {
    if (!rec) return false;
    const items = buildItems(rec.items, values);
    if (items.some((i) => [i.cantidad, i.cantidad_packs, i.cantidad_sueltos].some((x) => Number.isNaN(x)))) {
      if (!silent) toast.error('Hay cantidades inválidas (marcadas en rojo)');
      return 'invalid';
    }
    setSaving(true);
    try {
      const { data } = await recuentosAPI.update(id, { items, responsable: rec.responsable, comentario: rec.comentario });
      setRec(data);
      setDirty(false);
      localStorage.removeItem(draftKey(id));
      if (!silent) toast.success(isCorrection ? 'Corrección guardada' : 'Borrador guardado');
      return true;
    } catch (e) {
      if (!silent) toast.error(errorMsg(e, 'Error al guardar'));
      return 'error';
    } finally {
      setSaving(false);
    }
  }, [rec, values, id, isCorrection]);

  useEffect(() => {
    if (!dirty || !rec || rec.estado !== 'borrador') return undefined;
    clearTimeout(autosaveRef.current);
    autosaveRef.current = setTimeout(() => save(true), 4000);
    return () => clearTimeout(autosaveRef.current);
  }, [values, dirty, rec, save]);

  const onChange = (ingId, v) => {
    setValues((prev) => {
      const next = { ...prev, [ingId]: v };
      if (rec?.estado === 'borrador') localStorage.setItem(draftKey(id), JSON.stringify({ base: rec.updated_at, values: next }));
      return next;
    });
    setDirty(true);
  };

  const pedirEnvio = async () => {
    const ok = await save(true);
    if (ok === 'invalid') return toast.error('Corrige las cantidades inválidas antes de enviar');
    if (ok !== true) return toast.error('No se pudo guardar. Revisa tu conexión e intenta de nuevo.');
    const { data } = await recuentosAPI.validar(id);
    setValidacion(data);
  };

  const confirmarEnvio = async () => {
    setSending(true);
    try {
      await recuentosAPI.enviar(id);
      toast.success('Recuento enviado');
      setValidacion(null);
      navigate(basePath);
    } catch (e) {
      toast.error(errorMsg(e, 'Error al enviar'));
    } finally {
      setSending(false);
    }
  };

  const grouped = useMemo(() => {
    if (!rec) return [];
    const term = search.trim().toLowerCase();
    const items = rec.items.filter((i) => !term || i.nombre_justo.toLowerCase().includes(term));
    const zonas = [...new Set([...ZONAS, ...items.map((i) => i.zona)])];
    return zonas.map((z) => ({ zona: z, items: items.filter((i) => i.zona === z) })).filter((g) => g.items.length);
  }, [rec, search]);

  if (!rec) return <div className="p-6 text-slate-500">Cargando...</div>;
  const contados = rec.items.filter((i) => values[i.ingrediente_id] && isCounted(i, values[i.ingrediente_id])).length;
  const bottomOffset = basePath.startsWith('/local') ? 64 : 0;

  return (
    <div className="max-w-3xl mx-auto pb-28" data-testid="recuento-form-page">
      <div className="px-4 pt-4 space-y-3">
        <button type="button" onClick={() => navigate(basePath)} className="text-sm text-slate-500 flex items-center gap-1 hover:text-orange-600" data-testid="recuento-volver-btn">
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>
        <div className="flex items-start justify-between gap-2">
          <div>
            <h1 className="text-xl font-bold text-slate-800" data-testid="recuento-lugar">{rec.lugar_nombre}</h1>
            <p className="text-xs text-slate-500">{formatFechaHora(rec.created_at)} · {rec.user_name}</p>
          </div>
          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${ESTADOS[rec.estado]?.className}`} data-testid="recuento-estado">{ESTADOS[rec.estado]?.label}</span>
        </div>
        {!editable && (
          <div className="flex items-center gap-2 text-sm rounded-lg bg-slate-100 text-slate-600 px-3 py-2" data-testid="recuento-bloqueado-msg">
            <Lock className="w-4 h-4" /> Recuento enviado. Solo el administrador puede reabrirlo o corregirlo.
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Responsable" value={rec.responsable || ''} disabled={!editable}
            onChange={(e) => { setRec({ ...rec, responsable: e.target.value }); setDirty(true); }} data-testid="recuento-responsable-input" />
          <Input placeholder="Comentario (opcional)" value={rec.comentario || ''} disabled={!editable}
            onChange={(e) => { setRec({ ...rec, comentario: e.target.value }); setDirty(true); }} data-testid="recuento-comentario-input" />
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9 h-11" placeholder="Buscar ingrediente..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="recuento-buscar-input" />
        </div>
      </div>

      <div className="mt-3">
        {grouped.map((g) => (
          <section key={g.zona} data-testid={`zona-${g.zona}`}>
            <div className="sticky z-10 px-4 py-2 bg-orange-50/95 backdrop-blur border-y border-orange-100 flex justify-between" style={{ top: headerH }}>
              <span className="font-bold text-orange-700 text-sm uppercase tracking-wide">{g.zona}</span>
              <span className="text-xs text-orange-700">
                {g.items.filter((i) => isCounted(i, values[i.ingrediente_id])).length}/{g.items.length}
              </span>
            </div>
            <div className="px-4 py-2 space-y-2">
              {g.items.map((i) => (
                <IngredienteRow key={i.ingrediente_id} item={i} value={values[i.ingrediente_id]} disabled={!editable}
                  onChange={(v) => onChange(i.ingrediente_id, v)} />
              ))}
            </div>
          </section>
        ))}
      </div>

      <div className="fixed left-0 right-0 z-30 bg-white border-t border-slate-200 shadow-[0_-4px_12px_rgba(0,0,0,0.05)]" style={{ bottom: bottomOffset }}>
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-800" data-testid="recuento-progreso">{contados} de {rec.items.length} contados</div>
            <div className="h-1.5 bg-slate-100 rounded-full mt-1 overflow-hidden">
              <div className="h-full bg-orange-500 transition-[width] duration-300" style={{ width: `${(contados / Math.max(rec.items.length, 1)) * 100}%` }} />
            </div>
            <div className="text-[11px] text-slate-400 mt-0.5">{saving ? 'Guardando...' : dirty ? 'Cambios sin guardar' : 'Guardado'}</div>
          </div>
          {editable && (
            <Button variant="outline" onClick={() => save(false)} disabled={saving} className="h-11" data-testid="recuento-guardar-btn">
              <Save className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">{isCorrection ? 'Guardar corrección' : 'Guardar borrador'}</span>
            </Button>
          )}
          {rec.estado === 'borrador' && (
            <Button onClick={pedirEnvio} disabled={saving} className="h-11 bg-orange-500 hover:bg-orange-600" data-testid="recuento-enviar-btn">
              <Send className="w-4 h-4 mr-1" /> Enviar
            </Button>
          )}
        </div>
      </div>

      <EnviarDialog open={!!validacion} onOpenChange={(o) => !o && setValidacion(null)} validacion={validacion} onConfirm={confirmarEnvio} sending={sending} />
    </div>
  );
}
